<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { AuthService } from "../../../../lib/api/auth.service";
  import Button from "../../../Button.svelte";
  import Input from "../../../Input.svelte";
  import SearchInput from "../../../SearchInput.svelte";
  import { MyEmployerService, type InviteEmployerRequest } from "../../../../lib/api/employee/myEmployer.service";

  export let selectedEmployer = "";
  export let leaveHours = "";
  export let employerOptions: Array<{ uuid: string; name: string; status?: string }> = [];
  export let loading = false;

  const dispatch = createEventDispatcher();

  // Validation and error states
  let employerError = "";
  let hoursError = "";
  let touchedEmployer = false;
  let touchedHours = false;

  // Add employer modal state
  let showAddEmployerModal = false;
  let addingEmployer = false;
  let newEmployerName = "";
  let newEmployerEmail = "";
  let newEmployerPhone = "";
  let addEmployerError = "";
  
  // Search existing employers in add modal
  let searchingExistingEmployers = false;
  let existingEmployerSearchResults: Array<{ uuid: string; name: string; isLinked: boolean }> = [];
  let existingEmployerSearchTimeout: ReturnType<typeof setTimeout> | undefined;

  // Link to existing employer modal state
  let showLinkEmployerModal = false;
  let linkingEmployer = false;
  let employerSearchQuery = "";
  let searchResults: Array<{ uuid: string; name: string }> = [];
  let searchingEmployers = false;
  let linkEmployerError = "";

  // Common hour presets for quick selection
  const hourPresets = [4, 8, 16, 24, 40];

  function handleNext() {
    // Mark fields as touched
    touchedEmployer = true;
    touchedHours = true;

    // Validate
    validateEmployer();
    validateHours();

    if (!selectedEmployer || !leaveHours || employerError || hoursError) {
      return;
    }
    dispatch("next");
  }

  function handleCancel() {
    dispatch("cancel");
  }

  function handleError(message: string) {
    dispatch("error", { message });
  }


  function handleHoursChange(event: Event) {
    const target = event.target as HTMLInputElement;
    leaveHours = target.value;
    touchedHours = true;
    validateHours();
  }

  function handlePresetClick(hours: number) {
    leaveHours = hours.toString();
    touchedHours = true;
    validateHours();
  }

  function validateEmployer() {
    if (!selectedEmployer) {
      employerError = "Please select an employer";
    } else {
      employerError = "";
    }
  }

  function validateHours() {
    const hours = parseInt(leaveHours);
    if (!leaveHours || leaveHours.trim() === "") {
      hoursError = "Please enter the number of hours";
    } else if (isNaN(hours) || hours <= 0) {
      hoursError = "Hours must be greater than 0";
    } else if (hours < 1) {
      hoursError = "Minimum donation request is 1 hour";
    } else if (hours > 1000) {
      hoursError = "Maximum donation request is 1000 hours";
    } else {
      hoursError = "";
    }
  }

  function handleEmployerSelect(uuid: string) {
    selectedEmployer = uuid;
    touchedEmployer = true;
    validateEmployer();
  }

  function openAddEmployerModal() {
    showAddEmployerModal = true;
    newEmployerName = "";
    newEmployerEmail = "";
    newEmployerPhone = "";
    addEmployerError = "";
    existingEmployerSearchResults = [];
  }

  async function searchExistingEmployersInAddModal() {
    if (existingEmployerSearchTimeout) {
      clearTimeout(existingEmployerSearchTimeout);
    }

    const searchQuery = newEmployerName?.trim() || '';
    if (!searchQuery || searchQuery.length < 2) {
      existingEmployerSearchResults = [];
      searchingExistingEmployers = false;
      return;
    }

    existingEmployerSearchTimeout = setTimeout(async () => {
      // Re-check the query value in case it changed during the timeout
      const currentQuery = newEmployerName?.trim() || '';
      if (!currentQuery || currentQuery.length < 2) {
        existingEmployerSearchResults = [];
        searchingExistingEmployers = false;
        return;
      }

      searchingExistingEmployers = true;
      
      try {
        const user = AuthService.getUser();
        if (!user?.uuid) {
          console.error('No user UUID found');
          existingEmployerSearchResults = [];
          searchingExistingEmployers = false;
          return;
        }

        console.log('Searching for employers with query:', currentQuery);
        const result = await MyEmployerService.searchAllEmployers(user.uuid, currentQuery);
        console.log('Search result:', result);
        
        if (result.success && result.data) {
          // Get UUIDs of employers already in the user's list
          const existingUuids = new Set(employerOptions.map(e => e.uuid));
          
          // Filter out already-linked employers and limit to 3 results
          existingEmployerSearchResults = result.data
            .filter(emp => !existingUuids.has(emp.uuid))
            .map(emp => ({ 
              uuid: emp.uuid, 
              name: emp.name,
              isLinked: false
            }))
            .slice(0, 3); // Limit to 3 results
          console.log('Filtered results:', existingEmployerSearchResults);
        } else {
          console.log('Search failed or no data:', result.message);
          existingEmployerSearchResults = [];
        }
      } catch (err: any) {
        console.error('Error searching employers:', err);
        existingEmployerSearchResults = [];
      } finally {
        searchingExistingEmployers = false;
      }
    }, 300);
  }

  // Watch for name changes to trigger search (only when modal is open)
  $: searchTrigger = showAddEmployerModal ? newEmployerName : '';
  $: if (showAddEmployerModal && searchTrigger) {
    const nameLength = searchTrigger.trim().length;
    if (nameLength >= 2) {
      searchExistingEmployersInAddModal();
    } else {
      existingEmployerSearchResults = [];
      searchingExistingEmployers = false;
      if (existingEmployerSearchTimeout) {
        clearTimeout(existingEmployerSearchTimeout);
        existingEmployerSearchTimeout = undefined;
      }
    }
  }

  function closeAddEmployerModal() {
    showAddEmployerModal = false;
    newEmployerName = "";
    newEmployerEmail = "";
    newEmployerPhone = "";
    addEmployerError = "";
    existingEmployerSearchResults = [];
    if (existingEmployerSearchTimeout) {
      clearTimeout(existingEmployerSearchTimeout);
    }
  }

  function openLinkEmployerModal() {
    showLinkEmployerModal = true;
    employerSearchQuery = "";
    searchResults = [];
    linkEmployerError = "";
  }

  function closeLinkEmployerModal() {
    showLinkEmployerModal = false;
    employerSearchQuery = "";
    searchResults = [];
    linkEmployerError = "";
  }

  let searchTimeout: ReturnType<typeof setTimeout>;
  async function handleEmployerSearch() {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    if (!employerSearchQuery.trim()) {
      searchResults = [];
      return;
    }

    searchTimeout = setTimeout(async () => {
      searchingEmployers = true;
      linkEmployerError = "";
      
      try {
        const user = AuthService.getUser();
        if (!user?.uuid) {
          linkEmployerError = "User not found";
          return;
        }

        const result = await MyEmployerService.searchAllEmployers(user.uuid, employerSearchQuery.trim());
        
        if (result.success && result.data) {
          // Filter out employers already in the user's list
          const existingUuids = new Set(employerOptions.map(e => e.uuid));
          searchResults = result.data
            .filter(emp => !existingUuids.has(emp.uuid))
            .map(emp => ({ uuid: emp.uuid, name: emp.name }));
        } else {
          searchResults = [];
          linkEmployerError = result.message || "No employers found";
        }
      } catch (err: any) {
        linkEmployerError = err.message || "Failed to search employers";
        searchResults = [];
      } finally {
        searchingEmployers = false;
      }
    }, 300);
  }

  async function handleLinkEmployer(employerUuid: string, employerName: string) {
    linkingEmployer = true;
    linkEmployerError = "";
    
    try {
      const user = AuthService.getUser();
      if (!user?.uuid) {
        linkEmployerError = "User not found";
        return;
      }

      const result = await MyEmployerService.linkToExistingEmployer(user.uuid, employerUuid);
      
      if (result.success) {
        // Store the name to select after refresh
        employerNameToSelect = employerName;
        
        // Close modal first
        closeLinkEmployerModal();
        
        // Trigger refresh - the reactive statement will handle selection
        dispatch("refresh-employers");
      } else {
        linkEmployerError = result.message || "Failed to link employer";
        if (result.errors) {
          const firstError = Object.values(result.errors)[0];
          if (Array.isArray(firstError) && firstError.length > 0) {
            linkEmployerError = firstError[0];
          }
        }
      }
    } catch (err: any) {
      linkEmployerError = err.message || "An unexpected error occurred";
    } finally {
      linkingEmployer = false;
    }
  }

  async function handleLinkEmployerFromAddModal(employerUuid: string, employerName: string) {
    addingEmployer = true;
    addEmployerError = "";
    
    try {
      const user = AuthService.getUser();
      if (!user?.uuid) {
        addEmployerError = "User not found";
        return;
      }

      const result = await MyEmployerService.linkToExistingEmployer(user.uuid, employerUuid);
      
      if (result.success) {
        // Store the name to select after refresh
        employerNameToSelect = employerName;
        
        // Close modal first
        closeAddEmployerModal();
        
        // Trigger refresh - the reactive statement will handle selection
        dispatch("refresh-employers");
      } else {
        addEmployerError = result.message || "Failed to link employer";
        if (result.errors) {
          const firstError = Object.values(result.errors)[0];
          if (Array.isArray(firstError) && firstError.length > 0) {
            addEmployerError = firstError[0];
          }
        }
      }
    } catch (err: any) {
      addEmployerError = err.message || "An unexpected error occurred";
    } finally {
      addingEmployer = false;
    }
  }

  // Track employer name to auto-select after creation
  let employerNameToSelect: string | null = null;

  // Reactive statement to auto-select employer when it appears in the list
  $: if (employerNameToSelect && employerOptions.length > 0) {
    const newEmployer = employerOptions.find(opt => 
      opt.name.toLowerCase() === employerNameToSelect!.toLowerCase()
    );
    if (newEmployer) {
      handleEmployerSelect(newEmployer.uuid);
      employerNameToSelect = null; // Reset after selection
    }
  }

  async function createEmployer() {
    if (!newEmployerName.trim() || !newEmployerEmail.trim()) {
      addEmployerError = "Employer name and email are required";
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmployerEmail.trim())) {
      addEmployerError = "Please enter a valid email address";
      return;
    }

    addingEmployer = true;
    addEmployerError = "";

    try {
      const requestData: InviteEmployerRequest = {
        name: newEmployerName.trim(),
        contact_name: newEmployerName.trim(),
        contact_email: newEmployerEmail.trim(),
        contact_phone: newEmployerPhone.trim() || undefined,
      };

      const result = await MyEmployerService.inviteEmployer(requestData);

      if (result.success) {
        // Store the name to select after refresh
        employerNameToSelect = newEmployerName.trim();
        
        // Close modal first
        closeAddEmployerModal();
        
        // Dispatch success message to parent
        dispatch("show-notification", {
          message: "An invitation has been sent to your employer, we will notify you when they are onboard.",
          type: "success"
        });
        
        // Trigger refresh - the reactive statement will handle selection
        dispatch("refresh-employers");
      } else {
        // Handle API errors
        let errorMsg = result.message || "Failed to create employer";
        if (result.errors && typeof result.errors === "object") {
          if (result.errors.errors) {
            const firstError = Object.values(result.errors.errors)[0];
            if (Array.isArray(firstError) && firstError.length > 0) {
              errorMsg = firstError[0];
            }
          }
        }
        addEmployerError = errorMsg;
      }
    } catch (err: any) {
      addEmployerError = err.message || "An unexpected error occurred";
    } finally {
      addingEmployer = false;
    }
  }

  // Validation state
  $: isValidHours = leaveHours && !hoursError && parseInt(leaveHours) > 0;
  $: isValidForm = selectedEmployer && isValidHours && !employerError && !hoursError;
</script>

<div class="step-container">
  <!-- Header -->
  <div class="step-header">
    <button class="back-button" on:click={handleCancel} disabled={loading}>
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <path d="M19 12H6m6-6l-6 6 6 6" />
      </svg>
    </button>
    <div class="header-content">
      <div class="header-indicator"></div>
      <h2 class="step-title">New Donation Request</h2>
      <p class="step-subtitle">Choose the amount of leave you want to request for donation</p>
    </div>
    <div class="header-spacer"></div>
  </div>

  <!-- Form Fields -->
  <div class="form-content">
    <!-- Employer Selection -->
    <fieldset class="form-group employer-selection">
      <legend class="form-label">
        Select Employer <span class="required">*</span>
      </legend>
      
      <!-- Employer Tiles -->
      <div class="employer-tiles">
        {#each employerOptions as employer}
          <button
            type="button"
            class="employer-tile"
            class:selected={selectedEmployer === employer.uuid}
            on:click={() => handleEmployerSelect(employer.uuid)}
            disabled={loading}
          >
            <span class="employer-name">{employer.name}</span>
            {#if employer.status === 'seeded'}
              <span class="seeded-badge">Awaiting registration</span>
            {/if}
            {#if selectedEmployer === employer.uuid}
              <svg class="check-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            {/if}
          </button>
        {/each}
        
        <!-- Link to Existing Employer Tile -->
        <button
          type="button"
          class="employer-tile link-employer-tile"
          on:click={openLinkEmployerModal}
          disabled={loading}
        >
          <svg class="link-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
          </svg>
          <span class="link-label">Link to Existing Employer</span>
        </button>
        
        <!-- Add New Employer Tile -->
        <button
          type="button"
          class="employer-tile add-employer-tile"
          on:click={openAddEmployerModal}
          disabled={loading}
        >
          <svg class="add-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          <span class="add-label">Add New Employer</span>
        </button>
      </div>

      {#if touchedEmployer && employerError}
        <p class="error-message" role="alert">{employerError}</p>
      {/if}
      {#if !touchedEmployer && employerOptions.length > 0}
        <p class="form-hint">Select the employer associated with your leave</p>
      {/if}
    </fieldset>

    <!-- Leave Hours Selection -->
    <div class="form-group">
      <label for="hours-input" class="form-label">
        Leave Hours to Donate <span class="required">*</span>
      </label>
      
      <!-- Hours Input with Inline Presets -->
      <div class="hours-input-container">
        <div class="hours-input-wrapper">
          <Input
            type="number"
            value={leaveHours}
            placeholder="Enter hours"
            on:input={handleHoursChange}
            disabled={loading}
            required={true}
            min="1"
            max="1000"
            step="1"
            error={touchedHours && hoursError ? hoursError : ""}
          />
        </div>
        
        <!-- Compact Preset Buttons -->
        <div class="preset-buttons-inline">
          {#each hourPresets as preset}
            <button
              type="button"
              class="preset-button-inline"
              class:active={leaveHours === preset.toString()}
              on:click={() => handlePresetClick(preset)}
              disabled={loading}
            >
              {preset}h
            </button>
          {/each}
        </div>
      </div>
      
      <div class="hint-container">
        {#if touchedHours && hoursError}
          <p class="error-message" role="alert">{hoursError}</p>
        {:else if !touchedHours}
          <p class="form-hint">Enter the number of leave hours you want to request for donation (minimum 1 hour)</p>
        {/if}
      </div>
    </div>
  </div>

  <!-- Action Buttons -->
  <div class="step-actions">
    <Button
      variant="primary"
      fullWidth={true}
      on:click={handleNext}
      disabled={loading || !isValidForm}
    >
      {loading ? "Processing..." : "Continue to Select Charity"}
    </Button>
    <Button
      variant="secondary"
      fullWidth={true}
      on:click={handleCancel}
      disabled={loading}
    >
      Cancel
    </Button>
  </div>
</div>

<!-- Add Employer Modal -->
{#if showAddEmployerModal}
  <div 
    class="modal-overlay" 
    on:click|self={closeAddEmployerModal}
    role="presentation"
  >
    <div class="modal-container" role="dialog" aria-modal="true" aria-labelledby="add-employer-title">
      <div class="modal-content">
        <div class="modal-header">
          <h2 id="add-employer-title" class="modal-title">Add New Employer</h2>
          <button class="modal-close" on:click={closeAddEmployerModal} disabled={addingEmployer}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          {#if addEmployerError}
            <div class="error-message" role="alert">{addEmployerError}</div>
          {/if}

          <div class="form-field">
            <Input
              label="employer-name"
              placeholder="Enter employer name"
              bind:value={newEmployerName}
              on:input={() => {
                // Trigger search when typing (reactive statement will handle it)
                if (showAddEmployerModal && newEmployerName.trim().length >= 2) {
                  // The reactive statement will handle the search
                } else if (showAddEmployerModal) {
                  existingEmployerSearchResults = [];
                  searchingExistingEmployers = false;
                }
              }}
              fullWidth={true}
              disabled={addingEmployer}
              required={true}
            />
            <!-- Show existing employers that can be linked -->
            {#if searchingExistingEmployers}
              <div class="loading-state-inline">
                <div class="loading-spinner-small"></div>
                <p>Searching for existing employers...</p>
              </div>
          {:else if existingEmployerSearchResults.length > 0}
            <div class="existing-employers-section">
              <p class="existing-employers-label">Found existing employers:</p>
              <div class="existing-employers-list">
                {#each existingEmployerSearchResults as employer}
                  {#if employer.isLinked}
                    <div class="existing-employer-item linked-employer-item" role="status">
                      <span class="employer-name-text">{employer.name}</span>
                      <span class="already-linked-badge">Already Linked</span>
                    </div>
                  {:else}
                    <button
                      type="button"
                      class="existing-employer-item"
                      on:click={() => handleLinkEmployerFromAddModal(employer.uuid, employer.name)}
                      disabled={addingEmployer || linkingEmployer}
                    >
                      <span class="employer-name-text">{employer.name}</span>
                      <svg class="link-icon-small" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                      </svg>
                      <span class="link-text">Link</span>
                    </button>
                  {/if}
                {/each}
              </div>
            </div>
            {:else if newEmployerName.trim().length >= 2 && !searchingExistingEmployers}
              <p class="no-results-hint">No existing employers found. You can create a new one below.</p>
            {/if}
          </div>

          <div class="form-field">
            <Input
              label="employer-email"
              type="email"
              placeholder="Enter contact email"
              bind:value={newEmployerEmail}
              fullWidth={true}
              disabled={addingEmployer}
              required={true}
            />
          </div>

          <div class="form-field">
            <Input
              label="employer-phone"
              placeholder="Enter phone number (optional)"
              bind:value={newEmployerPhone}
              fullWidth={true}
              disabled={addingEmployer}
            />
          </div>
        </div>

        <div class="modal-actions">
          <Button 
            variant="primary" 
            fullWidth={true}
            on:click={createEmployer}
            disabled={addingEmployer || !newEmployerName.trim() || !newEmployerEmail.trim()}
          >
            {addingEmployer ? "Creating..." : "Create & Select"}
          </Button>
          <Button 
            variant="secondary" 
            fullWidth={true}
            on:click={closeAddEmployerModal}
            disabled={addingEmployer}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  </div>
{/if}

<!-- Link to Existing Employer Modal -->
{#if showLinkEmployerModal}
  <div 
    class="modal-overlay" 
    on:click|self={closeLinkEmployerModal}
    role="presentation"
  >
    <div class="modal-container" role="dialog" aria-modal="true" aria-labelledby="link-employer-title">
      <div class="modal-content">
        <div class="modal-header">
          <h2 id="link-employer-title" class="modal-title">Link to Existing Employer</h2>
          <button class="modal-close" on:click={closeLinkEmployerModal} disabled={linkingEmployer}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <p class="modal-description">
            Search for an existing employer organization and link yourself to it. This prevents duplicate entries.
          </p>

          {#if linkEmployerError}
            <div class="error-message" role="alert">{linkEmployerError}</div>
          {/if}

          <div class="form-field">
            <SearchInput
              placeholder="Search by employer name..."
              bind:value={employerSearchQuery}
              on:input={handleEmployerSearch}
              on:search={handleEmployerSearch}
              width="100%"
            />
          </div>

          {#if searchingEmployers}
            <div class="loading-state">
              <div class="loading-spinner-small"></div>
              <p>Searching...</p>
            </div>
          {:else if searchResults.length > 0}
            <div class="search-results">
              <h3 class="results-title">Found Employers</h3>
              <div class="results-list">
                {#each searchResults as employer}
                  <button
                    type="button"
                    class="result-item"
                    on:click={() => handleLinkEmployer(employer.uuid, employer.name)}
                    disabled={linkingEmployer}
                  >
                    <span class="result-name">{employer.name}</span>
                    <svg class="link-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M5 12h14M12 5l7 7-7 7"></path>
                    </svg>
                  </button>
                {/each}
              </div>
            </div>
          {:else if employerSearchQuery.trim() && !searchingEmployers}
            <div class="no-results">
              <p>No employers found matching "{employerSearchQuery}"</p>
            </div>
          {/if}
        </div>

        <div class="modal-actions">
          <Button 
            variant="secondary" 
            fullWidth={true}
            on:click={closeLinkEmployerModal}
            disabled={linkingEmployer}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .step-container {
    padding: 1.5rem;
    max-height: 80vh;
    overflow-y: auto;
    background-color: #f9fafb;
  }

  .step-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 2rem;
  }

  .back-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 50%;
    color: #6b7280;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 0.5rem;
  }

  .back-button:hover:not(:disabled) {
    background-color: #f3f4f6;
    color: #374151;
  }

  .back-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .header-content {
    flex: 1;
    text-align: center;
    margin: 0 1rem;
  }

  .header-spacer {
    width: 40px; /* Same width as back button for centering */
  }

  .header-indicator {
    width: 50px;
    height: 4px;
    background-color: #e5e7eb;
    border-radius: 2px;
    margin: 0 auto 1.5rem;
  }

  .step-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 0.5rem 0;
  }

  .step-subtitle {
    color: #6b7280;
    font-size: 1rem;
    margin: 0;
    line-height: 1.4;
  }

  .form-content {
    margin-bottom: 2rem;
  }

  .form-group {
    margin-bottom: 1.5rem;
    margin-top: 1.5rem;
  }

  fieldset.form-group {
    border: none;
    padding: 0;
    min-width: 0;
  }

  .form-group:last-child {
    margin-bottom: 0;
  }

  .form-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.5rem;
  }

  .required {
    color: #ef4444;
    margin-left: 0.25rem;
  }

  .hint-container {
    min-height: 1.5rem;
    margin-top: 0.5rem;
  }

  .form-hint {
    font-size: 0.75rem;
    color: #6b7280;
    margin: 0;
    line-height: 1.4;
  }

  .error-message {
    font-size: 0.75rem;
    color: #ef4444;
    margin: 0.5rem 0 0 0;
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  .error-message::before {
    content: "⚠";
    font-size: 0.875rem;
  }

  .step-actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  /* Hours Input Container */
  .hours-input-container {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  /* Hours Input */
  .hours-input-wrapper {
    width: 200px;
    min-width: 200px;
    max-width: 200px;
    flex-shrink: 0;
  }

  .hours-input-wrapper :global(.input-wrapper) {
    width: 100% !important;
    min-width: 200px !important;
    max-width: 200px !important;
  }

  /* Hide label in input wrapper since we have a label above */
  .hours-input-wrapper :global(.input-label) {
    display: none;
  }

  /* Fix input field width to prevent resizing */
  .hours-input-wrapper :global(.input-field) {
    width: 100% !important;
    min-width: 200px !important;
    max-width: 200px !important;
    box-sizing: border-box;
  }

  /* Inline Preset Buttons */
  .preset-buttons-inline {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    flex-shrink: 0;
    margin-top: 0.5rem; /* Align with input field (below label) */
  }

  .preset-button-inline {
    padding: 0.5rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: white;
    color: #374151;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  .preset-button-inline:hover:not(:disabled) {
    border-color: var(--color-primary);
    background: #fffbf0;
    color: var(--color-primary);
  }

  .preset-button-inline.active {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: white;
  }

  .preset-button-inline:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .preset-button-inline:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.1);
  }

  /* Ensure consistent rounded corners for all form inputs */
  .form-group :global(.dropdown-container),
  .form-group :global(.input-container),
  .form-group :global(input),
  .form-group :global(select) {
    border-radius: 8px !important;
  }

  /* Employer Tiles */
  .employer-tiles {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 0.75rem;
    margin-bottom: 0.5rem;
  }

  .employer-tile {
    padding: 1rem;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: space-between;
    text-align: left;
    min-height: 60px;
  }

  .employer-tile:hover:not(:disabled) {
    border-color: var(--color-primary);
    background: #fffbf0;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(255, 180, 0, 0.15);
  }

  .employer-tile.selected {
    border-color: var(--color-primary);
    background: #fffbf0;
    box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.1);
  }

  .employer-tile:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .employer-tile:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.2);
  }

  .employer-name {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    flex: 1;
  }

  .seeded-badge {
    display: block;
    font-size: 0.7rem;
    color: #92400e;
    background: #fef3c7;
    border-radius: 9999px;
    padding: 0.125rem 0.5rem;
    margin-top: 0.25rem;
    font-weight: 500;
  }

  .check-icon {
    color: var(--color-primary);
    flex-shrink: 0;
    margin-left: 0.5rem;
  }

  .add-employer-tile {
    border-style: dashed;
    border-color: #d1d5db;
    flex-direction: row;
    gap: 0.5rem;
    justify-content: center;
    align-items: center;
    color: #6b7280;
    min-height: 60px;
  }

  .add-employer-tile:hover:not(:disabled) {
    border-color: var(--color-primary);
    color: var(--color-primary);
    background: #fffbf0;
  }

  .add-icon {
    color: currentColor;
    flex-shrink: 0;
  }

  .add-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: currentColor;
  }

  /* Modal Styles */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
  }

  .modal-container {
    background-color: white;
    width: 100%;
    max-width: 480px;
    border-radius: 12px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    overflow: hidden;
    border: 1px solid #e5e7eb;
    margin: 1rem;
  }

  .modal-content {
    padding: 2rem;
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
  }

  .modal-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .modal-close {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
    color: #6b7280;
    border-radius: 8px;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .modal-close:hover:not(:disabled) {
    background-color: #f3f4f6;
    color: #374151;
  }

  .modal-close:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .modal-body {
    margin-bottom: 1.5rem;
  }

  .form-field {
    margin-bottom: 1.5rem;
  }

  .form-field:last-child {
    margin-bottom: 0;
  }

  .modal-actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  /* Responsive design */
  @media (max-width: 768px) {
    .step-container {
      padding: 1rem;
    }

    .step-title {
      font-size: 1.25rem;
    }

    .step-subtitle {
      font-size: 0.875rem;
    }

    .employer-tiles {
      grid-template-columns: 1fr;
    }

    .modal-container {
      margin: 0.5rem;
    }

    .modal-content {
      padding: 1.5rem;
    }
  }

  .modal-description {
    font-size: 0.875rem;
    color: #6b7280;
    margin-bottom: 1.5rem;
    line-height: 1.5;
  }

  .loading-state {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-top: 1rem;
    padding: 1rem;
    justify-content: center;
    color: #6b7280;
    font-size: 0.875rem;
  }

  .loading-spinner-small {
    width: 16px;
    height: 16px;
    border: 2px solid #e5e7eb;
    border-top-color: var(--color-primary, #f0b429);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .search-results {
    margin-top: 1.5rem;
    max-height: 300px;
    overflow-y: auto;
  }

  .results-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin: 0 0 0.75rem 0;
  }

  .results-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .result-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: left;
    width: 100%;
  }

  .result-item:hover:not(:disabled) {
    border-color: var(--color-primary, #f0b429);
    background-color: #fffbeb;
  }

  .result-item:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .result-name {
    font-weight: 500;
    color: #111827;
  }

  .link-arrow {
    color: var(--color-primary, #f0b429);
    flex-shrink: 0;
  }

  .no-results {
    margin-top: 1rem;
    padding: 1rem;
    text-align: center;
    color: #6b7280;
    font-size: 0.875rem;
  }

  .loading-state-inline {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
    padding: 0.5rem;
    color: #6b7280;
    font-size: 0.875rem;
  }

  .existing-employers-section {
    margin-top: 1rem;
    padding: 1rem;
    background-color: #f0f9ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
  }

  .existing-employers-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #1e40af;
    margin: 0 0 0.75rem 0;
  }

  .existing-employers-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .existing-employer-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    background: white;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: left;
    width: 100%;
    gap: 0.75rem;
  }

  .existing-employer-item:hover:not(:disabled) {
    border-color: #3b82f6;
    background-color: #eff6ff;
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
  }

  .existing-employer-item:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .employer-name-text {
    flex: 1;
    font-weight: 500;
    color: #111827;
    text-align: left;
  }

  .link-icon-small {
    color: #3b82f6;
    flex-shrink: 0;
  }

  .link-text {
    font-size: 0.875rem;
    font-weight: 500;
    color: #3b82f6;
    flex-shrink: 0;
  }

  .linked-employer-item {
    cursor: default;
    opacity: 0.7;
  }

  .linked-employer-item:hover {
    border-color: #bfdbfe;
    background-color: white;
    box-shadow: none;
  }

  .already-linked-badge {
    font-size: 0.75rem;
    font-weight: 500;
    color: #059669;
    background-color: #d1fae5;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    flex-shrink: 0;
  }

  .no-results-hint {
    font-size: 0.75rem;
    color: #6b7280;
    margin: 0.5rem 0 0 0;
    font-style: italic;
  }
</style>
